import React, { useState } from "react";
import { Checkbox, Field, Text } from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import { type ConnectionEditState, type ConnectionEditStateUpdater } from "@onetasklist/shared";
import {
  useGitHubSourceSettingsText,
  useGitHubSourceSettingsViewModel,
} from "@onetasklist/shared/sources/github";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { SourceMultiTagPicker } from "./components/SourceMultiTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";

interface GitHubSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
}

export const GitHubSourceSettings: React.FC<GitHubSourceSettingsProps> = ({
  editingConnection,
  onUpdate,
  sectionOffset,
}) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const text = useGitHubSourceSettingsText(t);
  const vm = useGitHubSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });

  const [repositoriesQuery, setRepositoriesQuery] = useState("");

  if (!vm.isVisible) {
    return null;
  }

  return (
    <SettingsSectionCard
      sectionOffset={sectionOffset}
      title={t(
        "board.connections.catalogue.sourceSettings.github.sectionRepositories",
      )}
    >
      <Field
        orientation="horizontal"
        label={text.repositoriesLabelText}
        hint={text.repositoriesHintText}
      >
        <SourceMultiTagPicker
          selectedIds={vm.selectedRepositoryIds}
          query={repositoriesQuery}
          placeholder={text.repositoriesPlaceholderText}
          options={vm.repositoryOptions}
          onQueryChange={setRepositoriesQuery}
          onSelectionChange={vm.onSelectRepositories}
          minQueryLength={0}
          typeToSearchText={text.repositoriesHintText}
          isLoading={!vm.repositoriesLoaded}
        />
      </Field>

      <Field
        orientation="horizontal"
        label={text.assignedToMeLabelText}
        hint={text.assignedToMeHintText}
      >
        <Checkbox
          checked={vm.assignedToMeFixed}
          disabled
          label={text.assignedToMeLabelText}
        />
      </Field>

      {!vm.repositoriesLoaded && <Text>{text.loadingText}</Text>}
      {vm.hasError && <Text>{text.loadErrorText}</Text>}
    </SettingsSectionCard>
  );
};
